import type { ApiWeighingSlip } from '@Model';
import {
    type Awaitable,
    Badge,
    Button,
    ButtonWithConfirmationModal,
    Card,
    ContextMenu,
    defaultIconSize,
    EnumMessageLevel,
    EnumMessageType,
    FlexColumn,
    Grid,
    GridColumn,
    GridColumns,
    iconSizeLg,
    TextInput,
    type ISubFrameBodyProps,
    LabeledDiv,
    renderDecimal,
    UnlabeledField,
    useGuiBinder,
    NumberInput,
} from '@synqo/frontend-component-library';
import { IconDotsVertical, IconTrash } from '@tabler/icons-react';
import { getShortUnitLabel } from '@Model';
import { WeighingButton } from './Weighing/WeighingButton';
import { useWeighingSlipEdit } from './Weighing/useWeighingSlipEdit';

export interface WeighingSlipProps extends ISubFrameBodyProps<ApiWeighingSlip> {
    delete: () => Awaitable<boolean>;
}

export function WeighingSlip(props: WeighingSlipProps) {
    const apiWeighingSlip: ApiWeighingSlip = props.data;

    const binder = useGuiBinder<ApiWeighingSlip>(props);
    const {
        hasWeights,
        doWeighIn,
        doWeighOut,
        deductionShown,
        showDeduction,
        removeDeduction,
        externalNumberShown,
        showExternalNumber,
    } = useWeighingSlipEdit({
        apiWeighingSlip,
        requestUpdateState: props.requestUpdateState,
    });

    const weightUnitLabel = getShortUnitLabel(apiWeighingSlip.unit);

    return (
        <Card
            name={`weighing_slip_${apiWeighingSlip.id}`}
            border={'withoutBorder'}
            fullyRounded
            variant="container"
            padding="md"
        >
            {externalNumberShown && (
                <Grid columns={15}>
                    <GridColumn span={5}>
                        <TextInput {...binder.bindToProp('external_id')} label="Externe Nummber" />
                    </GridColumn>
                </Grid>
            )}
            <Grid columns={15}>
                <GridColumns spans={[3, 2, 3, 2, 3, 1, 1]}>
                    <NumberInput
                        {...binder.bindToProp('gross_weight')}
                        decimals={3}
                        key="gross"
                        label={`Brutto in ${weightUnitLabel}`}
                    />
                    <WeighingButton
                        key="weighIn"
                        allowedUnit={hasWeights ? apiWeighingSlip.unit : undefined}
                        doWeighing={doWeighIn}
                    />
                    <NumberInput
                        {...binder.bindToProp('tare_weight')}
                        decimals={3}
                        key="tare"
                        label={`Tara in ${weightUnitLabel}`}
                    />
                    <WeighingButton
                        key="weighOut"
                        allowedUnit={hasWeights ? apiWeighingSlip.unit : undefined}
                        doWeighing={doWeighOut}
                    />
                    <LabeledDiv htmlFor="net_weight" label={`Netto in ${weightUnitLabel}`}>
                        <Badge key="net_weight" variant="primary" size="sm" type="input">
                            {renderDecimal(apiWeighingSlip.net_weight, 3)}
                        </Badge>
                    </LabeledDiv>
                    <UnlabeledField>
                        <ContextMenu
                            name="weighing_slip_options"
                            icon={() => <IconDotsVertical size={iconSizeLg} />}
                            locked={!props.canEdit}
                        >
                            <FlexColumn justify="start" align="stretch">
                                {deductionShown || (
                                    <Button onClick={showDeduction} locked={!props.canEdit} fullWidth>
                                        Abzug hinzufügen
                                    </Button>
                                )}
                                {externalNumberShown || (
                                    <Button onClick={showExternalNumber} locked={!props.canEdit} fullWidth>
                                        Externe Nummer erfassen
                                    </Button>
                                )}
                            </FlexColumn>
                        </ContextMenu>
                    </UnlabeledField>
                    <UnlabeledField>
                        <ButtonWithConfirmationModal
                            label="Wiegeschein entfernen"
                            tooltip="Wiegeschein entfernen"
                            onClick={props.delete}
                            appearance={'outlined'}
                            type="square"
                            confirmModalMessage={{
                                title: 'Wiegeschein entfernen',
                                message: `Möchten Sie den Wiegeschein WS${apiWeighingSlip.number} löschen?`,
                                level: EnumMessageLevel.Warning,
                                type: EnumMessageType.MultipleChoice,
                            }}
                            locked={!props.canEdit}
                            modalButtonContent={
                                <>
                                    <IconTrash size={defaultIconSize} />
                                    &nbsp;Löschen
                                </>
                            }
                        >
                            <IconTrash size={defaultIconSize} />
                        </ButtonWithConfirmationModal>
                    </UnlabeledField>
                </GridColumns>
            </Grid>
            {deductionShown && (
                <Grid columns={14}>
                    <GridColumns spans={[2, 8, 4]}>
                        <NumberInput
                            {...binder.bindToProp('deduction')}
                            label={`Abzug in ${weightUnitLabel}`}
                            decimals={3}
                            locked={apiWeighingSlip.deduction_percentage > 0}
                        />
                        <TextInput {...binder.bindToProp('deduction_reason')} label="Grund für Abzug" required />
                        <UnlabeledField>
                            <Button onClick={removeDeduction} appearance="outlined" locked={!props.canEdit}>
                                Abzug entfernen
                            </Button>
                        </UnlabeledField>
                    </GridColumns>
                </Grid>
            )}
        </Card>
    );
}
